import AccountPermissions from "@dashboard/components/AccountPermissions/AccountPermissions";
import { type AtticErrorFragment, type PermissionEnum } from "@dashboard/graphql";
import { type PermissionData } from "@dashboard/permissionGroups/components/PermissionGroupDetailsPage/PermissionGroupDetailsPage";
import { Box } from "@saleor/macaw-ui-next";
import { type ChangeEvent } from "react";
import { useIntl } from "react-intl";

import { getAtticApiKeyErrorMessage, getAtticApiKeyFieldError } from "../../errors";
import { formMessages } from "../../messages";

export interface AtticApiKeyPermissionsCardProps {
  /** The grantable permissions: MANAGE_APPS left out, the viewer's lacks disabled. */
  choices: PermissionData[];
  selected: PermissionEnum[];
  errors: AtticErrorFragment[];
  disabled: boolean;
  onChange: (permissions: PermissionEnum[]) => void;
}

/**
 * The Dashboard's own permission picker, as custom apps and permission groups
 * use it. "Full access" means every permission the viewer can give, which is
 * all the backend would accept anyway.
 */
export const AtticApiKeyPermissionsCard = ({
  choices,
  selected,
  errors,
  disabled,
  onChange,
}: AtticApiKeyPermissionsCardProps) => {
  const intl = useIntl();
  const grantable = choices.filter(choice => !choice.disabled).map(choice => choice.code);
  const hasFullAccess = grantable.length > 0 && grantable.every(code => selected.includes(code));
  const error = getAtticApiKeyFieldError(errors, "permissions");
  // The picker reports two changes per click (the codes and the full-access
  // flag); the flag is derived here, so only the codes are taken.
  const handleChange = (event: ChangeEvent<any>) => {
    if (event.target.name === "permissions") {
      onChange(event.target.value as PermissionEnum[]);
    }
  };

  return (
    <Box data-test-id="attic-api-key-permissions">
      <AccountPermissions
        permissions={choices}
        permissionsExceeded={false}
        data={{ hasFullAccess, permissions: selected }}
        disabled={disabled}
        disabledPermissionsTooltip={intl.formatMessage(formMessages.permissionDisabled)}
        description={intl.formatMessage(formMessages.permissionsDescription)}
        fullAccessLabel={intl.formatMessage(formMessages.fullAccess)}
        errorMessage={getAtticApiKeyErrorMessage(error, intl)}
        onChange={handleChange}
      />
    </Box>
  );
};

export default AtticApiKeyPermissionsCard;
